// Authentication screens: login, first-run setup (#/setup) and forced password change.
// Includes the small isometric building illustration (inline SVG built with DOM APIs).
import { h, svg, field, input, button, icon, clear, iconButton } from '../dom.js';
import { t } from '../i18n/fr.js';
import { formatDuration, PALETTE } from '../format.js';
import { setCsrf } from '../api.js';

const USERNAME_RE = /^[a-z0-9._-]{2,32}$/;
/** Same cap as server/auth/passwords.js. */
const MAX_PASSWORD_LENGTH = 1024;

/** True for a valid username (SPEC §7: lowercase [a-z0-9._-]{2,32}). */
export function isValidUsername(u) {
  return USERNAME_RE.test(String(u ?? ''));
}

/**
 * Client-side mirror of the server strength rules (SPEC §7). Returns the failed rule ids:
 * 'length' (< 10 chars), 'mix' (only letters or only digits), 'username' (equals the username).
 * @param {string} pw
 * @param {string} [username]
 * @returns {string[]}
 */
export function passwordProblems(pw, username = '') {
  const s = String(pw ?? '');
  const problems = [];
  const len = [...s].length;
  if (len < 10 || len > MAX_PASSWORD_LENGTH) problems.push('length');
  if (s && (/^\p{L}+$/u.test(s) || /^\p{N}+$/u.test(s))) problems.push('mix');
  if (username && s.trim().toLowerCase() === String(username).trim().toLowerCase()) problems.push('username');
  return problems;
}

/**
 * Normalises a lockout delay to seconds (the server sends seconds; values that look like
 * milliseconds are converted).
 */
export function lockoutSeconds(retryAfter) {
  const n = Number(retryAfter);
  if (!Number.isFinite(n) || n <= 0) return 15 * 60;
  return n > 86400 ? Math.ceil(n / 1000) : Math.ceil(n);
}

// ---------------------------------------------------------------------------------------------
// Isometric building illustration

const COS30 = Math.cos(Math.PI / 6);
const iso = (x, y, z) => [(x - y) * COS30, (x + y) * 0.5 - z];

function boxFaces(x, y, z, w, d, hgt) {
  const P = (px, py, pz) => iso(px, py, pz);
  return {
    top: [P(x, y, z + hgt), P(x + w, y, z + hgt), P(x + w, y + d, z + hgt), P(x, y + d, z + hgt)],
    right: [P(x + w, y, z + hgt), P(x + w, y + d, z + hgt), P(x + w, y + d, z), P(x + w, y, z)],
    left: [P(x, y + d, z + hgt), P(x + w, y + d, z + hgt), P(x + w, y + d, z), P(x, y + d, z)],
  };
}

/**
 * Draws a small isometric office building: stacked floor slabs, coloured pods, a glass elevator.
 * @returns {SVGSVGElement}
 */
export function buildingIllustration() {
  const shapes = [];
  const bounds = { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity };
  const poly = (pts, cls) => {
    for (const [x, y] of pts) {
      bounds.minX = Math.min(bounds.minX, x); bounds.maxX = Math.max(bounds.maxX, x);
      bounds.minY = Math.min(bounds.minY, y); bounds.maxY = Math.max(bounds.maxY, y);
    }
    shapes.push(svg('polygon', { points: pts.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' '), class: cls }));
  };
  const box = (x, y, z, w, d, hgt, cls) => {
    const f = boxFaces(x, y, z, w, d, hgt);
    poly(f.left, `${cls} face-left`);
    poly(f.right, `${cls} face-right`);
    poly(f.top, `${cls} face-top`);
  };
  const S = 150;
  // Lower floors show their front pods (the ones visible under the next slab); the top floor is full.
  const podsPerFloor = [
    [[84, 14, 'teal'], [14, 84, 'sand'], [84, 84, 'coral']],
    [[84, 14, 'sky'], [14, 84, 'peach'], [84, 84, 'lavender']],
    [[14, 14, 'sage'], [84, 14, 'coral'], [14, 84, 'periwinkle'], [84, 84, 'sand']],
  ];
  const floors = 3;
  const gap = 58;
  for (let f = 0; f < floors; f += 1) {
    const z = f * gap;
    box(0, 0, z, S, S, 8, 'ill-slab');
    // central hall
    box(60, 60, z + 8, 30, 30, 3, 'ill-hall');
    for (const [px, py, color] of podsPerFloor[f]) {
      const c = PALETTE.includes(color) ? color : 'sand';
      box(px, py, z + 8, 52, 52, 5, `ill-pod ill-${c}`);
      // desks with tiny people
      for (const [dx, dy] of [[10, 12], [30, 12], [10, 32], [30, 32]]) {
        box(px + dx, py + dy, z + 13, 10, 6, 5, 'ill-desk');
        const [hx, hy] = iso(px + dx + 5, py + dy + 11, z + 22);
        shapes.push(svg('circle', { cx: hx.toFixed(1), cy: hy.toFixed(1), r: 2.3, class: 'ill-head' }));
      }
    }
  }
  // Elevator shaft through every floor (drawn last, in front).
  box(67, 67, 0, 16, 16, (floors - 1) * gap + 30, 'ill-shaft');
  const pad = 10;
  const vb = [bounds.minX - pad, bounds.minY - pad, bounds.maxX - bounds.minX + pad * 2, bounds.maxY - bounds.minY + pad * 2]
    .map((n) => n.toFixed(1)).join(' ');
  return svg('svg', {
    class: 'building-ill', viewBox: vb, role: 'img', 'aria-label': t('login.illustration'), focusable: 'false',
  }, ...shapes);
}

// ---------------------------------------------------------------------------------------------
// Screens

function passwordInput(props) {
  const el = input({ type: 'password', ...props });
  const toggle = iconButton('eye', t('login.showPassword'), { class: 'pw-toggle', variant: 'ghost', size: 16 });
  toggle.addEventListener('click', () => {
    const show = el.type === 'password';
    el.type = show ? 'text' : 'password';
    toggle.setAttribute('aria-label', show ? t('login.hidePassword') : t('login.showPassword'));
    toggle.title = toggle.getAttribute('aria-label');
    toggle.replaceChildren(icon(show ? 'eyeOff' : 'eye', { size: 16 }));
  });
  const wrap = h('div', { class: 'pw-wrap' }, el, toggle);
  wrap.input = el;
  return wrap;
}

function rulesList(getPw, getUser) {
  const items = ['length', 'mix', 'username'].map((id) => {
    const li = h('li', { class: 'rule', dataset: { rule: id } }, icon('check', { size: 14 }), h('span', {}, t(`password.rule.${id}`)));
    return [id, li];
  });
  const list = h('ul', { class: 'pw-rules', 'aria-live': 'polite' }, items.map(([, li]) => li));
  list.refresh = () => {
    const problems = passwordProblems(getPw(), getUser());
    for (const [id, li] of items) li.classList.toggle('is-ok', !!getPw() && !problems.includes(id));
  };
  return list;
}

function shell(root, { title, subtitle, form, footer }) {
  clear(root);
  root.className = 'auth-root';
  const brand = h('div', { class: 'auth-brand' },
    h('div', { class: 'auth-ill' }, buildingIllustration()),
    h('p', { class: 'auth-wordmark' }, t('app.wordmark')),
    h('p', { class: 'auth-tagline mono-label' }, t('app.tagline')));
  const card = h('main', { class: 'auth-card', id: 'main' },
    h('h1', { class: 'auth-title' }, title),
    subtitle ? h('p', { class: 'auth-subtitle' }, subtitle) : null,
    form,
    footer || null);
  root.appendChild(h('div', { class: 'auth' }, brand, card));
  // The autofocus attribute is not honoured on inserted nodes: focus explicitly.
  requestAnimationFrame(() => root.querySelector('[autofocus]')?.focus({ preventScroll: true }));
}

function errorBox() {
  const el = h('p', { class: 'auth-error', role: 'alert', hidden: true });
  el.show = (msg) => { el.textContent = msg || ''; el.hidden = !msg; };
  return el;
}

/**
 * Renders the login / setup / forced password change screen into `root`.
 * @param {HTMLElement} root
 * @param {{mode: 'login'|'setup'|'change', api: object, me?: object, token?: string, notice?: string,
 *   onDone: (result?: object) => void, onLogout?: () => void, onSwitch?: (mode: string) => void}} opts
 * @returns {{destroy(): void}}
 */
export function renderAuthScreen(root, opts) {
  const timers = new Set();
  const destroy = () => { for (const id of timers) clearInterval(id); timers.clear(); };
  if (opts.mode === 'setup') renderSetup(root, opts);
  else if (opts.mode === 'change') renderChange(root, opts);
  else renderLogin(root, opts, timers);
  return { destroy };
}

function renderLogin(root, { api, notice, onDone, onSwitch, setupRequired }, timers) {
  const user = input({ name: 'username', autocomplete: 'username', autocapitalize: 'none', spellcheck: false, required: true, autofocus: true });
  const pw = passwordInput({ name: 'password', autocomplete: 'current-password', required: true });
  const err = errorBox();
  const submit = button(t('login.submit'), { variant: 'primary', size: 'lg', type: 'submit', class: 'auth-submit' });
  let lockTimer = null;

  const lockout = (seconds) => {
    let left = lockoutSeconds(seconds);
    submit.disabled = true;
    const tick = () => {
      if (left <= 0) {
        clearInterval(lockTimer);
        timers.delete(lockTimer);
        submit.disabled = false;
        err.show(t('login.unlocked'));
        return;
      }
      // Whole minutes, and only rewritten when the text changes: the box is a live alert region,
      // so a per-second countdown would be read aloud every second.
      const text = t('login.locked', { duration: formatDuration(Math.max(1, Math.ceil(left / 60)) * 60000) });
      if (err.textContent !== text || err.hidden) err.show(text);
      left -= 1;
    };
    tick();
    lockTimer = setInterval(tick, 1000);
    timers.add(lockTimer);
  };

  const noticeEl = notice ? h('p', { class: 'auth-notice', role: 'status' }, notice) : null;
  const form = h('form', {
    class: 'auth-form', novalidate: true,
    onSubmit: async (e) => {
      e.preventDefault();
      err.show('');
      if (noticeEl) noticeEl.hidden = true; // "session expired" / "logged out" no longer applies
      const username = user.value.trim().toLowerCase();
      const password = pw.input.value;
      if (!username || !password) { err.show(t('login.invalid')); return; }
      submit.disabled = true;
      submit.classList.add('is-busy');
      try {
        const res = await api.post('/api/auth/login', { username, password });
        if (res?.csrf) setCsrf(res.csrf);
        onDone(res);
      } catch (ex) {
        if (ex.status === 429) { lockout(ex.retryAfter); return; }
        err.show(ex.status === 401 ? t('login.invalid') : ex.message);
        pw.input.select?.();
      } finally {
        submit.classList.remove('is-busy');
        if (!lockTimer || !timers.has(lockTimer)) submit.disabled = false;
      }
    },
  },
  noticeEl,
  field(t('login.username'), user),
  field(t('login.password'), pw),
  err, submit);
  const footer = h('p', { class: 'auth-footer' }, t('login.footer'),
    setupRequired ? h('button', { type: 'button', class: 'link-btn', onClick: () => onSwitch?.('setup') }, ` ${t('login.setupLink')}`) : null);
  shell(root, { title: t('login.title'), subtitle: t('login.subtitle'), form, footer });
}

/** Clears a field's error as soon as one of the inputs changes again. */
function clearOnInput(fieldEl, ...inputs) {
  for (const el of inputs) el?.addEventListener?.('input', () => fieldEl.setError(null));
}

function renderSetup(root, { api, token = '', onDone, onSwitch }) {
  const tokenIn = input({ name: 'token', autocomplete: 'off', spellcheck: false, value: token, required: true, autofocus: !token });
  const user = input({ name: 'username', autocomplete: 'username', autocapitalize: 'none', spellcheck: false, required: true, autofocus: !!token });
  const display = input({ name: 'displayName', autocomplete: 'name', required: true });
  const pw = passwordInput({ name: 'new-password', autocomplete: 'new-password', required: true });
  const pw2 = passwordInput({ name: 'confirm-password', autocomplete: 'new-password', required: true });
  const rules = rulesList(() => pw.input.value, () => user.value.trim().toLowerCase());
  pw.input.addEventListener('input', rules.refresh);
  user.addEventListener('input', rules.refresh);
  const userField = field(t('setup.username'), user, { hint: t('setup.usernameHint') });
  const pw2Field = field(t('setup.confirm'), pw2);
  clearOnInput(pw2Field, pw.input, pw2.input);
  const err = errorBox();
  const submit = button(t('setup.submit'), { variant: 'primary', size: 'lg', type: 'submit', class: 'auth-submit' });
  const form = h('form', {
    class: 'auth-form', novalidate: true,
    onSubmit: async (e) => {
      e.preventDefault();
      err.show('');
      userField.setError(null);
      pw2Field.setError(null);
      const username = user.value.trim().toLowerCase();
      if (!isValidUsername(username)) { userField.setError(t('setup.usernameInvalid')); user.focus(); return; }
      if (passwordProblems(pw.input.value, username).length) { err.show(t('password.weak')); rules.refresh(); return; }
      if (pw.input.value !== pw2.input.value) { pw2Field.setError(t('password.mismatch')); return; }
      submit.disabled = true;
      try {
        const res = await api.post('/api/auth/setup', {
          token: tokenIn.value.trim(), username, displayName: display.value.trim() || username, password: pw.input.value,
        });
        if (res?.csrf) setCsrf(res.csrf);
        onDone(res);
      } catch (ex) {
        err.show(ex.message);
      } finally {
        submit.disabled = false;
      }
    },
  },
  field(t('setup.token'), tokenIn), userField, field(t('setup.displayName'), display),
  field(t('setup.password'), pw), rules, pw2Field, err, submit);
  const footer = h('p', { class: 'auth-footer' },
    h('button', { type: 'button', class: 'link-btn', onClick: () => onSwitch?.('login') }, t('setup.backToLogin')));
  shell(root, { title: t('setup.title'), subtitle: t('setup.subtitle'), form, footer });
}

function renderChange(root, { api, me, onDone, onLogout }) {
  const username = me?.username || '';
  const current = passwordInput({ name: 'current-password', autocomplete: 'current-password', required: true, autofocus: true });
  const next = passwordInput({ name: 'new-password', autocomplete: 'new-password', required: true });
  const next2 = passwordInput({ name: 'confirm-password', autocomplete: 'new-password', required: true });
  const rules = rulesList(() => next.input.value, () => username);
  next.input.addEventListener('input', rules.refresh);
  const next2Field = field(t('change.confirm'), next2);
  clearOnInput(next2Field, next.input, next2.input);
  const err = errorBox();
  const submit = button(t('change.submit'), { variant: 'primary', size: 'lg', type: 'submit', class: 'auth-submit' });
  const form = h('form', {
    class: 'auth-form', novalidate: true,
    onSubmit: async (e) => {
      e.preventDefault();
      err.show('');
      next2Field.setError(null);
      if (passwordProblems(next.input.value, username).length) { err.show(t('password.weak')); rules.refresh(); return; }
      if (next.input.value !== next2.input.value) { next2Field.setError(t('password.mismatch')); return; }
      submit.disabled = true;
      try {
        await api.post('/api/auth/password', { current: current.input.value, next: next.input.value });
        onDone({ changed: true });
      } catch (ex) {
        err.show(ex.message);
      } finally {
        submit.disabled = false;
      }
    },
  },
  // Hidden username field helps password managers associate the new password.
  h('input', { type: 'text', name: 'username', autocomplete: 'username', value: username, hidden: true, readOnly: true }),
  field(t('change.current'), current), field(t('change.next'), next), rules, next2Field, err, submit);
  const footer = h('p', { class: 'auth-footer' },
    h('button', { type: 'button', class: 'link-btn', onClick: () => onLogout?.() }, t('change.logout')));
  shell(root, { title: t('change.title'), subtitle: t('change.subtitle'), form, footer });
}

/**
 * The password change form used from the user menu (modal version of the forced screen).
 * @param {{api: object, overlay: object, toast: Function, me: object}} ctx
 */
export function openPasswordModal(ctx) {
  const username = ctx.store?.get().me?.username || '';
  const current = passwordInput({ name: 'current-password', autocomplete: 'current-password', required: true, autofocus: true });
  const next = passwordInput({ name: 'new-password', autocomplete: 'new-password', required: true });
  const next2 = passwordInput({ name: 'confirm-password', autocomplete: 'new-password', required: true });
  const rules = rulesList(() => next.input.value, () => username);
  next.input.addEventListener('input', rules.refresh);
  const next2Field = field(t('change.confirm'), next2);
  clearOnInput(next2Field, next.input, next2.input);
  const err = errorBox();
  let layer = null;
  const submit = async (e) => {
    e?.preventDefault();
    err.show('');
    next2Field.setError(null);
    if (passwordProblems(next.input.value, username).length) { err.show(t('password.weak')); rules.refresh(); return; }
    if (next.input.value !== next2.input.value) { next2Field.setError(t('password.mismatch')); return; }
    layer.setBusy(true);
    try {
      await ctx.api.post('/api/auth/password', { current: current.input.value, next: next.input.value });
      ctx.toast.success(t('change.done'));
      layer.close('done');
    } catch (ex) {
      err.show(ex.message);
    } finally {
      layer.setBusy(false);
    }
  };
  const form = h('form', { class: 'stack', novalidate: true, onSubmit: submit },
    h('input', { type: 'text', name: 'username', autocomplete: 'username', value: username, hidden: true, readOnly: true }),
    field(t('change.currentShort'), current), field(t('change.next'), next), rules, next2Field, err);
  layer = ctx.overlay.openModal({
    title: t('menu.password'), size: 'sm', content: form,
    footer: [button(t('common.cancel'), { variant: 'ghost', onClick: () => layer.close('cancel') }),
      button(t('common.save'), { variant: 'primary', onClick: submit })],
  });
  return layer;
}
